import { Box, Text } from '@wix/design-system';
import React from 'react';
import { SaleBanner as PlatformSaleBanner } from '@wix/premium-purchase-platform/@components';
import {
  EclipseBanner,
  type EclipseBannerProps,
  SaleTimer,
  useCurrentModule,
  useResellerBranding,
  useSaleBannerType,
  type SaleBannerType,
  extractNumericRate,
} from '@wix/package-picker-shared-eclipse';
import { EclipsePackagePickerModule } from '../../modules/eclipse-package-picker.module';
import s from './SaleBanner.scss';

const getPreTitleKey = (bannerType: SaleBannerType): string | undefined => {
  const keyMap: Partial<Record<SaleBannerType, string>> = {
    personal_multi_day: 'sale.banner.personal_sale_age_1',
    personal_one_day: 'sale.banner.personal_sale_age_2',
    personal_last_chance: 'sale.banner.personal_sale_age_3',
    seasonal_extended: 'sale.banner.extended',
    seasonal_regular: 'sale.banner.limited_time_only',
  };

  return keyMap[bannerType];
};

const getTitleKey = (bannerType: SaleBannerType): string => {
  const keyMap: Record<SaleBannerType, string> = {
    reseller: 'premium-purchase-plan.reseller_sale.header',
    permanent_initial: 'premium-purchase-plan.header.permanent_50.banner',
    permanent_second_upgrade:
      'premium-purchase-plan.header.permanent_50.second_upgrade.banner',
    last_day: 'sale.banner.last_day_countdown',
    personal_multi_day: 'sale.banner.on.select.plans.text',
    personal_one_day: 'sale.banner.on.select.plans.text',
    personal_last_chance: 'sale.banner.on.select.plans.text',
    seasonal_extended: 'sale.banner.on.select.plans.text',
    seasonal_regular: 'sale.banner.on.select.plans.text',
  };

  return keyMap[bannerType];
};

export interface SaleBannerProps {
  /** Overrides for the banner's outer container (e.g. the Quick Pay lightbox's own insets). */
  containerProps?: Partial<EclipseBannerProps>;
}

export const SaleBanner = ({ containerProps }: SaleBannerProps = {}) => {
  const { bannerType, isLoading: isSaleBannerTypeLoading } =
    useSaleBannerType();
  const module = useCurrentModule();
  const { data: discountData } =
    module.SDK.eclipseOfferingApiClient.hooks.useFetchOfferingDiscount({});
  const { resellerBrandingInfo } = useResellerBranding();
  const { Trans } = EclipsePackagePickerModule.internals.useTranslations();
  return (
    <PlatformSaleBanner.Root>
      {({ data, isLoading: isSaleBannerLoading }) => {
        const { rate: platformRate, timeLeft } = data ?? {};
        const rate = platformRate ?? discountData?.discount?.rate;

        if (
          isSaleBannerTypeLoading ||
          isSaleBannerLoading ||
          !bannerType ||
          !rate
        ) {
          return null;
        }

        // TODO: Ask from the platform to return the rate without the % symbol
        const numericRate = extractNumericRate(rate);

        const titleKey = getTitleKey(bannerType);
        const preTitleKey = getPreTitleKey(bannerType);

        const content = (
          <Text
            weight="normal"
            size="medium"
            skin="standard"
            light={false}
            className={s.saleBannerText}
          >
            {preTitleKey && (
              <>
                <Trans i18nKey={preTitleKey} />
                {' - '}
              </>
            )}
            <Trans
              i18nKey={titleKey}
              values={{
                percent: numericRate,
                days: timeLeft?.days,
                resellerName: resellerBrandingInfo?.name,
              }}
              components={[<Text weight="bold" className={s.saleBannerText} />]}
            />
          </Text>
        );

        if (bannerType === 'last_day' && timeLeft) {
          const timer = (
            <SaleTimer
              hours={timeLeft.hours}
              minutes={timeLeft.minutes}
              seconds={timeLeft.seconds}
            />
          );

          return (
            <EclipseBanner {...containerProps}>
              <Box
                align="center"
                gap="SP1"
                display="flex"
                textAlign="center"
                verticalAlign="middle"
              >
                {content}
                <Text
                  weight="normal"
                  size="medium"
                  skin="standard"
                  light={false}
                  className={s.saleBannerText}
                >
                  •
                </Text>
                {timer}
              </Box>
            </EclipseBanner>
          );
        }

        return <EclipseBanner {...containerProps}>{content}</EclipseBanner>;
      }}
    </PlatformSaleBanner.Root>
  );
};
